import { useForm, UseFormReturn } from "@/lib/form/form"
import {
  FormSchema,
  ParamSchema,
  ParamSchemaType,
  ParamValidationType,
} from "@/lib/form/schema"
import { breakdownValidator } from "@/lib/validators/breakdown"
import { createChoiceValidator } from "@/lib/validators/choice"
import { createLimitValidator } from "@/lib/validators/limit"
import * as y from "yup"
import { useEffect } from "react"
import { get } from "lodash"
import { safeNumber } from "@/lib/utils"
import { FormParamStatus } from "@/lib/form/types"

interface UseIPQCFormProps {
  schema?: FormSchema
  metadata?: Record<string, any>
  defaultValues?: Record<string, any>
  isHandover?: boolean
}

function isHandoverBypassStatus(status?: string): boolean {
  return (
    status === FormParamStatus.NOT_APPLICABLE ||
    status === FormParamStatus.BREAK_DOWN ||
    status === FormParamStatus.CHANGE_OVER ||
    status === FormParamStatus.POWER_FAILURE ||
    status === FormParamStatus.OTHER ||
    status === FormParamStatus.SUBMITTED
  )
}

function createValidator(): y.Schema | undefined {
  const fieldValidator = y.object({
    id: y.string().uuid(),
    value: safeNumber,
    status: y.string(),
    gradeLabel: y.string(),
    remark: y.string(),
    recordedAt: y.string().datetime(),
  })

  const paramValidator = y
    .object({})
    .concat(fieldValidator)
    .concat(
      y.object({
        window: y.array(fieldValidator),
      })
    )
    .test({
      name: "test-handover-mode",
      test(value, ctx) {
        const context = ctx.options.context

        if (!context?.isHandover) {
          return true
        }

        const schema = get(context.schema, ctx.path) as ParamSchema
        if (!schema) {
          return ctx.createError({
            message: `${ctx.path} invalid schema`,
          })
        }

        if (isHandoverBypassStatus(value.status)) {
          return true
        }

        if (schema.type === ParamSchemaType.VALUE_NUMBER) {
          if (typeof value.value !== "number" && !value.window?.length) {
            return ctx.createError({
              message: `${ctx.path} either value or window[i].value is required [${schema.type}]`,
            })
          }

          if (value.window?.length) {
            for (const [index, window] of value.window.entries()) {
              if (isHandoverBypassStatus(window.status)) {
                continue
              }

              if (typeof window.value !== "number") {
                return ctx.createError({
                  message: `${ctx.path}.window[${index}] window value is required [${schema.type}]`,
                })
              }
            }

            return true
          }

          if (typeof value.value !== "number") {
            return ctx.createError({
              message: `${ctx.path} value is required [${schema.type}]`,
            })
          }

          return true
        }

        if (!(value.value || value.window?.length)) {
          return ctx.createError({
            message: `${ctx.path} one of value or window[i].value is required [${schema.type}]`,
          })
        }

        if (value.window?.length) {
          for (const [index, window] of value.window.entries()) {
            if (isHandoverBypassStatus(window.status)) {
              continue
            }

            if (!window.value) {
              return ctx.createError({
                message: `${ctx.path}.window[${index}] window value is required [${schema.type}]`,
              })
            }
          }

          return true
        }

        if (!value.value) {
          return ctx.createError({
            message: `${ctx.path} value is required [${schema.type}]`,
          })
        }

        return true
      },
    })

  const validator = y.object({
    products: y.array(
      y.object({
        productionLines: y
          .array(
            y.object({
              id: y.string().required(),
              machines: y
                .array(
                  y.object({
                    id: y.string().required(),
                    internalProcesses: y
                      .array(
                        y.object({
                          id: y.string().required(),
                          parameters: y.array(paramValidator).required(),
                        })
                      )
                      .required(),
                  })
                )
                .required(),
            })
          )
          .required(),
      })
    ),
  })

  return validator
}

export interface UseIPQCFormReturn {
  form: UseFormReturn
  rebuildForm(params: UseIPQCFormRebuildParams): void
}

interface UseIPQCFormRebuildParams {
  schema?: FormSchema
  defaultValues?: any
  isHandover: boolean
}

const validator = createValidator()

export function useIPQCForm({
  schema,
  metadata,
  defaultValues,
  isHandover,
}: UseIPQCFormProps): UseIPQCFormReturn {
  const form = useForm({
    metadata: metadata,
    validator: validator,
    defaultValues,
    context: {
      isHandover,
      schema: schema,
    },
  })

  function init(params: UseIPQCFormRebuildParams) {
    form.unregister({ keepHandlers: true })
    const resetParams = {
      validator: validator,
      defaultValues: params.defaultValues || defaultValues,
      context: {
        isHandover: params.isHandover,
        schema: params.schema || schema,
      },
    }
    form.reset(resetParams)

    params.schema?.products.forEach((product, productIndex) => {
      const productPath = `products[${productIndex}]`

      form.register(`${productPath}.id`, {
        value: product.id,
      })

      product.productionLines.forEach((line, lineIndex) => {
        const linePath = `${productPath}.productionLines[${lineIndex}]`

        form.register(`${linePath}.id`, { value: line.id })

        line.machines.forEach((stage, stageIndex) => {
          const stagePath = `${linePath}.machines[${stageIndex}]`

          form.register(`${stagePath}.id`, {
            value: stage.id,
          })
          form.register(`${stagePath}.breakdown`, {
            value: [],
            validate: breakdownValidator,
          })

          stage.internalProcesses.forEach((proc, procIndex) => {
            const procPath = `${stagePath}.internalProcesses[${procIndex}]`

            form.register(`${procPath}.id`, {
              value: proc.id,
            })

            proc.parameters.forEach((param, paramIndex) => {
              const paramPath = `${procPath}.parameters[${paramIndex}]`
              form.register(paramPath, {
                value: {
                  id: param.id,
                },
              })

              if (
                param.window?.length &&
                param.validationType === ParamValidationType.LIMIT
              ) {
                param.window.forEach((window, windowIndex) => {
                  const path = `${paramPath}.window[${windowIndex}]`
                  const defaultValue = get(params.defaultValues, path)

                  form.register(path, {
                    value: {
                      id: window.id,
                      value: defaultValue?.value ?? window.value ?? "",
                      startTime: window.startTime,
                      status:
                        defaultValue?.status ??
                        window.status ??
                        FormParamStatus.PENDING,
                      endTime: window.endTime,
                      remark: defaultValue?.remark ?? window.remark,
                      recordedAt: defaultValue?.recordedAt ?? window.recordedAt,
                      gradeLabel: defaultValue?.gradeLabel ?? window.gradeLabel,
                      resample: defaultValue?.resample ?? window.resample,
                      resampleCleared:
                        defaultValue?.resampleCleared ?? window.resampleCleared,
                      sequenceOrder:
                        defaultValue?.sequenceOrder ?? window.sequenceOrder,
                      overtime: defaultValue?.overtime ?? window.overtime,
                      early: defaultValue?.early ?? window.early,
                    },
                    validate: createLimitValidator({
                      schema: param,
                      path,
                      form,
                    }),
                  })
                })
              } else if (
                param.window?.length &&
                param.validationType === ParamValidationType.CHOICE
              ) {
                param.window.forEach((window, windowIndex) => {
                  const path = `${paramPath}.window[${windowIndex}]`
                  const defaultValue = get(params.defaultValues, path)

                  form.register(path, {
                    value: {
                      id: window.id,
                      value: defaultValue?.value ?? window.value ?? "",
                      status:
                        defaultValue?.status ??
                        window.status ??
                        FormParamStatus.PENDING,
                      startTime: window.startTime,
                      endTime: window.endTime,
                      remark: defaultValue?.remark ?? window.remark,
                      recordedAt: defaultValue?.recordedAt ?? window.recordedAt,
                      gradeLabel: defaultValue?.gradeLabel ?? window.gradeLabel,
                      resample: defaultValue?.resample ?? window.resample,
                      resampleCleared:
                        defaultValue?.resampleCleared ?? window.resampleCleared,
                      sequenceOrder:
                        defaultValue?.sequenceOrder ?? window.sequenceOrder,
                      overtime: defaultValue?.overtime ?? window.overtime,
                      early: defaultValue?.early ?? window.early,
                    },
                    validate: createChoiceValidator({
                      schema: param,
                      path,
                      form,
                    }),
                  })
                })
              } else if (
                param.window?.length &&
                param.validationType === ParamValidationType.NONE
              ) {
                param.window.forEach((window, windowIndex) => {
                  const path = `${paramPath}.window[${windowIndex}]`
                  const defaultValue = get(params.defaultValues, path)

                  form.register(path, {
                    value: {
                      id: window.id,
                      value: defaultValue?.value ?? window.value ?? "",
                      status:
                        defaultValue?.status ??
                        window.status ??
                        FormParamStatus.PENDING,
                      startTime: window.startTime,
                      endTime: window.endTime,
                      remark: defaultValue?.remark ?? window.remark,
                      recordedAt: defaultValue?.recordedAt ?? window.recordedAt,
                      gradeLabel: defaultValue?.gradeLabel ?? window.gradeLabel,
                      resample: defaultValue?.resample ?? window.resample,
                      resampleCleared:
                        defaultValue?.resampleCleared ?? window.resampleCleared,
                      sequenceOrder:
                        defaultValue?.sequenceOrder ?? window.sequenceOrder,
                      overtime: defaultValue?.overtime ?? window.overtime,
                      early: defaultValue?.early ?? window.early,
                    },
                  })
                })
              } else if (param.type === ParamValidationType.CHOICE) {
                const defaultValue = get(params.defaultValues, paramPath)

                form.register(paramPath, {
                  value: {
                    id: param.id,
                    value: defaultValue?.value ?? param.value ?? "",
                    status:
                      defaultValue?.status ??
                      param.status ??
                      FormParamStatus.PENDING,
                    remark: defaultValue?.remark ?? param.remark,
                    recordedAt: defaultValue?.recordedAt ?? param.recordedAt,
                    gradeLabel: defaultValue?.gradeLabel ?? param.gradeLabel,
                    resample: defaultValue?.resample ?? param.resample,
                    resampleCleared:
                      defaultValue?.resampleCleared ?? param.resampleCleared,
                  },
                  validate: createChoiceValidator({
                    schema: param,
                    path: paramPath,
                    form,
                  }),
                })
              } else if (param.validationType === ParamValidationType.LIMIT) {
                const defaultValue = get(params.defaultValues, paramPath)

                form.register(paramPath, {
                  value: {
                    id: param.id,
                    value: defaultValue?.value ?? param.value ?? "",
                    status:
                      defaultValue?.status ??
                      param.status ??
                      FormParamStatus.PENDING,
                    remark: defaultValue?.remark ?? param.remark,
                    recordedAt: defaultValue?.recordedAt ?? param.recordedAt,
                    gradeLabel: defaultValue?.gradeLabel ?? param.gradeLabel,
                    resample: defaultValue?.resample ?? param.resample,
                    resampleCleared:
                      defaultValue?.resampleCleared ?? param.resampleCleared,
                  },
                  validate: createLimitValidator({
                    schema: param,
                    path: paramPath,
                    form,
                  }),
                })
              } else {
                const defaultValue = get(params.defaultValues, paramPath)

                form.register(paramPath, {
                  value: {
                    id: param.id,
                    value: defaultValue?.value ?? param.value ?? "",
                    status:
                      defaultValue?.status ??
                      param.status ??
                      FormParamStatus.PENDING,
                    remark: defaultValue?.remark ?? param.remark,
                    recordedAt: defaultValue?.recordedAt ?? param.recordedAt,
                    gradeLabel: defaultValue?.gradeLabel ?? param.gradeLabel,
                    resample: defaultValue?.resample ?? param.resample,
                    resampleCleared:
                      defaultValue?.resampleCleared ?? param.resampleCleared,
                  },
                })
              }
            })
          })
        })
      })
    })

    void form.runAllValidators()
  }

  useInit()

  function useInit() {
    useEffect(() => {
      init({ schema, defaultValues, isHandover: !!isHandover })

      return () => {
        form.unregister()
      }
    }, [form])
  }

  return {
    form,
    rebuildForm: init,
  }
}
